import { useState } from "react";
import { toast } from "sonner";
import { Check, Navigation, Phone, Undo2, Clock, AlertTriangle, Loader2, MessageCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { KIND, currentDelay, navLink, toHm, toMin } from "@/lib/route";
import { Button } from "@/components/ui/button";
import { Card, Pill } from "@/components/common";

const StopRow = ({ s, delay, onArrive, busy }) => {
  const diff = s.arrived_at ? toMin(s.arrived_at) - toMin(s.eta) : null;
  const updated = !s.arrived_at && delay ? toHm(toMin(s.eta) + delay) : null;
  return (
    <Card className={`p-4 sm:p-5 flex gap-4 ${s.arrived_at ? "bg-emerald-50/40 border-emerald-200" : ""}`} data-testid={`route-stop-${s.seq}`}>
      <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center font-bold text-white ${s.arrived_at ? "bg-emerald-600" : s.kind === "riconsegna" ? "bg-[#B8860B]" : s.kind === "ritiro" ? "bg-[#0D3B52]" : "bg-[#5C6B73]"}`}>
        {s.arrived_at ? <Check className="h-5 w-5" /> : s.seq}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Pill cls={KIND[s.kind]?.cls}>{KIND[s.kind]?.label}</Pill>
          <p className="font-semibold text-[#0D3B52] truncate">{s.label}</p>
        </div>
        <p className="text-sm text-[#5C6B73] mt-1">{s.address}</p>
        {s.note && <p className="text-xs font-semibold text-rose-700 mt-1">{s.note}</p>}
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm">
          <span data-testid={`route-stop-eta-${s.seq}`}><Clock className="inline h-3.5 w-3.5 mr-1 text-[#B8860B]" />Arrivo stimato <strong>{s.eta}</strong></span>
          {updated && <span className="text-amber-700" data-testid={`route-stop-updated-${s.seq}`}>Previsto ora <strong>{updated}</strong></span>}
          {s.arrived_at && (
            <span className="text-emerald-700" data-testid={`route-stop-real-${s.seq}`}>
              Arrivo reale <strong>{s.arrived_at}</strong> ({diff > 0 ? `+${diff}` : diff} min)
            </span>
          )}
          {s.window_from && <span className="text-[#5C6B73]">Fascia {s.window_from}–{s.window_to || "…"}</span>}
          <span className="text-[#5C6B73]">{s.leg_km} km · {s.leg_min} min di guida</span>
        </div>
        {s.wait_min > 0 && <p className="text-xs text-sky-700 mt-1">Attesa di {s.wait_min} min per rispettare la fascia oraria</p>}
        {s.late_min > 0 && <p className="text-xs text-rose-700 mt-1"><AlertTriangle className="inline h-3 w-3 mr-1" />Oltre la fascia di {s.late_min} min</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" onClick={() => onArrive(s, !!s.arrived_at)} disabled={busy} data-testid={`route-stop-arrive-${s.seq}`}
            className={`rounded-full ${s.arrived_at ? "bg-white text-[#0D3B52] border border-[#E2DDD5] hover:bg-[#FAF8F5]" : "bg-emerald-600 hover:bg-emerald-700"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : s.arrived_at ? <><Undo2 className="h-4 w-4 mr-1" />Annulla</> : <><Check className="h-4 w-4 mr-1" />Sono arrivato</>}
          </Button>
          <a href={navLink(s)} target="_blank" rel="noreferrer" data-testid={`route-stop-nav-${s.seq}`}
            className="inline-flex items-center rounded-full border border-[#E2DDD5] bg-white px-3 h-9 text-sm font-medium text-[#0D3B52] hover:bg-[#FAF8F5]">
            <Navigation className="h-4 w-4 mr-1" />Naviga
          </a>
          {s.whatsapp_url && (
            <a href={s.whatsapp_url} target="_blank" rel="noreferrer" data-testid={`route-stop-wa-${s.seq}`} className="inline-flex items-center rounded-full bg-[#25D366] hover:bg-[#1DA851] px-3 h-9 text-sm font-medium text-white">
              <MessageCircle className="h-4 w-4 mr-1" />Avvisa {s.finestra}
            </a>
          )}
          {s.phone && (
            <a href={`tel:${s.phone}`} className="inline-flex items-center rounded-full border border-[#E2DDD5] bg-white px-3 h-9 text-sm font-medium text-[#0D3B52] hover:bg-[#FAF8F5]">
              <Phone className="h-4 w-4 mr-1" />Chiama
            </a>
          )}
        </div>
      </div>
    </Card>
  );
};

export const RouteTimeline = ({ route, onChange }) => {
  const [busy, setBusy] = useState(null);
  const delay = currentDelay(route.stops);
  const arrive = async (s, undo) => {
    setBusy(s.seq);
    try {
      const r = await api.post(`/admin/percorso/${route.date}/tappe/${s.seq}/arrivo`, { undo });
      onChange(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-3" data-testid="route-timeline">
      <div className="flex items-center gap-3 text-sm text-[#5C6B73]">
        <span className="h-8 w-8 rounded-full bg-[#D4AF37] text-white font-bold flex items-center justify-center">A</span>
        Partenza alle <strong className="text-[#0D3B52]">{route.start_time}</strong> da {route.depot.address}
      </div>
      {delay !== 0 && (
        <p className={`text-sm font-semibold ${delay > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="route-delay">
          {delay > 0 ? `Sei in ritardo di ${delay} min: gli orari successivi sono aggiornati.` : `Sei in anticipo di ${-delay} min.`}
        </p>
      )}
      {route.stops.map((s) => <StopRow key={s.seq} s={s} delay={delay} onArrive={arrive} busy={busy === s.seq} />)}
      {route.roundtrip && (
        <div className="flex items-center gap-3 text-sm text-[#5C6B73]">
          <span className="h-8 w-8 rounded-full bg-[#D4AF37] text-white font-bold flex items-center justify-center">A</span>
          Rientro previsto alle <strong className="text-[#0D3B52]">{delay ? toHm(toMin(route.return_eta) + delay) : route.return_eta}</strong>
        </div>
      )}
    </div>
  );
};
